import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  getAktivitaGaKpiCompare,
  getAktivitaGaMeta,
  getAktivitaAhrefsOverview,
  getAktivitaGscOverview,
  type AktivitaCountry,
  type GaKpiCompare,
  type GaMeta,
  type AhrefsOverview,
  type GscOverview,
} from '../../api/rondo';
import { Card } from '../../components/arc/card/card';
import { CompactSelect } from '../../components/arc/compact-select/compact-select';
import { PageState } from '../../components/arc/page-state/page-state';
import { DeltaArrow, fmtNum, fmtDuration, fmtPct, fmtValue, type KpiFmt } from './aktivita-shared';
import './aktivita-prehled.css';

interface TileDef {
  key: keyof GaKpiCompare['current'];
  label: string;
  fmt: KpiFmt;
  lowerIsBetter?: boolean;
}

const ROW_MAIN: TileDef[] = [
  { key: 'sessions', label: 'Návštěvy (sessions)', fmt: 'num' },
  { key: 'page_views', label: 'Zobrazení stránek', fmt: 'num' },
  { key: 'avg_session_duration_sec', label: 'Avg. čas / session', fmt: 'duration' },
  { key: 'bounce_rate', label: 'Opuštění (bounce)', fmt: 'pct', lowerIsBetter: true },
];
const ROW_EXTRA: TileDef[] = [
  { key: 'active_users', label: 'Aktivní návštěvníci webu (GA4)', fmt: 'num' },
  { key: 'new_users', label: 'Noví uživatelé', fmt: 'num' },
  { key: 'engagement_rate', label: 'Engagement rate', fmt: 'pct' },
  { key: 'organic_search', label: 'Google vyhledávání', fmt: 'num' },
];

function tilePrevText(d: TileDef, prev: number): string {
  switch (d.fmt) {
    case 'duration':
      return fmtDuration(prev);
    case 'pct':
      return fmtPct(prev, 1);
    default:
      return fmtNum(prev);
  }
}

export function AktivitaPrehledPage() {
  const [country, setCountry] = useState<AktivitaCountry>('all');
  const [gaMetaText, setGaMetaText] = useState('-');

  return (
    <section className="rondo-activity-overview rondo-arc-activity-overview p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">AKTIVITA</div><h1>Přehled</h1><p>Návštěvnost, zapojení a výkon organických kanálů</p></div><CompactSelect ariaLabel="Trh" value={country} onChange={(value) => setCountry(value as AktivitaCountry)} options={[{ value: 'all', label: 'Všechny trhy' }, { value: 'cz', label: 'CZ' }, { value: 'sk', label: 'SK' }, { value: 'ro', label: 'RO' }]} /></header>

      {(country === 'sk' || country === 'ro') && (
        <div className="rondo-arc-activity-notice">
          rondo.{country} nemá GA tracking – data v této sekci jsou jen pro CZ. SEO data pro
          SK/RO najdeš v <a href="/aktivita/seo" className="alert-link">SEO (Ahrefs)</a> a{' '}
          <a href="/aktivita/vyhledavani" className="alert-link">Vyhledávání (GSC)</a>.
        </div>
      )}

      <KpiSection country={country} onMetaChange={setGaMetaText} />

      <div className="rondo-arc-activity-sources">
        <AhrefsCard country={country} />
        <GscCard country={country} />
      </div>

      <footer className="rondo-arc-activity-footer">
        <span>Detailní pohledy jsou dostupné v podsekcích Aktivity.</span>
        <span>{gaMetaText}</span>
      </footer>
    </section>
  );
}

function KpiSection({ country, onMetaChange }: { country: AktivitaCountry; onMetaChange: (text: string) => void }) {
  const [data, setData] = useState<GaKpiCompare | null>(null);
  const [meta, setMeta] = useState<GaMeta | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getAktivitaGaKpiCompare({ period: 'month', country })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
    getAktivitaGaMeta()
      .then((m) => !cancelled && setMeta(m))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [country]);

  const metaText = (() => {
    if (!meta) return '-';
    const c = meta.cache;
    if (!c || !c.last_synced_at) return 'GA: žádná data v cache (čekám na backfill)';
    return `GA sync ${c.last_synced_at} · ${c.days_in_cache} dní v cache`;
  })();

  useEffect(() => {
    onMetaChange(metaText);
  }, [metaText, onMetaChange]);

  return (
    <>
      {error ? <PageState title="Data GA se nepodařilo načíst" message={error} /> : (
        <>
          <div className="rondo-arc-activity-metrics">
            {ROW_MAIN.map((d) => (
              <Tile key={d.key as string} def={d} data={data} />
            ))}
          </div>
          <div className="rondo-arc-activity-metrics">
            {ROW_EXTRA.map((d) => (
              <Tile key={d.key as string} def={d} data={data} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

function Tile({ def, data }: { def: TileDef; data: GaKpiCompare | null }) {
  const curr = Number(data?.current?.[def.key] ?? 0);
  const prev = Number(data?.previous?.[def.key] ?? 0);
  return (
    <Card title={def.label} className="rondo-arc-activity-metric">
      <strong>{fmtValue(curr, def.fmt)}</strong>
      <p>{data ? <DeltaArrow curr={curr} prev={prev} lowerIsBetter={def.lowerIsBetter} /> : '—'} <span>oproti {tilePrevText(def, prev)}</span></p>
    </Card>
  );
}

function AhrefsCard({ country }: { country: AktivitaCountry }) {
  const [data, setData] = useState<AhrefsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAktivitaAhrefsOverview({ country })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [country]);

  const m = data?.metrics;
  const hasData = m && Object.keys(m).length > 0;

  return (
    <Card title="SEO · Ahrefs" className="rondo-arc-activity-source">
        <a href="/aktivita/seo" className="rondo-arc-activity-detail">Zobrazit SEO</a>
        <div className="rondo-arc-activity-source-body" id="ahrefs-overview">
          {loading ? (
            <div className="text-muted small">Načítám…</div>
          ) : error ? (
            <div className="text-muted small">Ahrefs: {error}</div>
          ) : !hasData ? (
            <div className="text-muted small">{data?.sync && data.sync.reason !== 'ok' && data.sync.message ? data.sync.message : 'Data dorazí po prvním Ahrefs sync (denně 05:00).'}</div>
          ) : (
            <div className="rondo-arc-activity-source-stats cols-3">
              <div>
                <div className="fs-3 fw-semibold">{Number(m!.domain_rating ?? 0).toFixed(1)}</div>
                <div className="text-muted small">Domain Rating</div>
              </div>
              <div>
                <div className="fs-3 fw-semibold">{fmtNum(m!.backlinks_total)}</div>
                <div className="text-muted small">Backlinks</div>
              </div>
              <div>
                <div className="fs-3 fw-semibold">{fmtNum(m!.refdomains)}</div>
                <div className="text-muted small">Ref. domény</div>
              </div>
            </div>
          )}
        </div>
    </Card>
  );
}

function GscCard({ country }: { country: AktivitaCountry }) {
  const [data, setData] = useState<GscOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAktivitaGscOverview({ period: 'month', country })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [country]);

  const k = data?.kpi;
  const hasData = k && (k.clicks || k.impressions || k.ctr || k.position);

  return (
    <Card title="Google Search Console" className="rondo-arc-activity-source">
        <a href="/aktivita/vyhledavani" className="rondo-arc-activity-detail">Zobrazit vyhledávání</a>
        <div className="rondo-arc-activity-source-body" id="gsc-overview">
          {loading ? (
            <div className="text-muted small">Načítám…</div>
          ) : error ? (
            <div className="text-muted small">GSC: {error}</div>
          ) : !hasData ? (
            <div className="text-muted small">
              Data dorazí až Dan přidá service account <code>rondo-analytics@rondo-491712</code> do
              Search Console properties.
            </div>
          ) : (
            <div className="rondo-arc-activity-source-stats cols-4">
              <div>
                <div className="fs-4 fw-semibold">{fmtNum(k!.clicks)}</div>
                <div className="text-muted small">Kliky</div>
              </div>
              <div>
                <div className="fs-4 fw-semibold">{fmtNum(k!.impressions)}</div>
                <div className="text-muted small">Imprese</div>
              </div>
              <div>
                <div className="fs-4 fw-semibold">{fmtPct(k!.ctr, 2)}</div>
                <div className="text-muted small">CTR</div>
              </div>
              <div>
                <div className="fs-4 fw-semibold">{Number(k!.position ?? 0).toFixed(1)}</div>
                <div className="text-muted small">Pozice</div>
              </div>
            </div>
          )}
        </div>
    </Card>
  );
}
